(() => {
  const root = document.documentElement;
  const loader = document.querySelector('[data-entry-loader]');
  if (!loader || !root.classList.contains('dr-entry-pending')) return;

  const KEY = 'desertRoseEntrySeenV3';
  const progress = loader.querySelector('[data-entry-progress]');
  const progressBar = loader.querySelector('[data-entry-progress-bar]');
  const progressTrack = loader.querySelector('[data-entry-progress-track]');
  const status = loader.querySelector('[data-entry-status]');
  const quote = loader.querySelector('[data-entry-quote]');
  const isFr = (document.documentElement.lang || '').toLowerCase().startsWith('fr');
  const runtime = window.DesertRoseRuntime || {};
  const isMobile = window.matchMedia?.('(max-width: 989px)').matches;
  const phrases = isFr
    ? [
        'NOTHING BLOOMS IN ITS COMFORT ZONE.',
        'BORN UNDER PRESSURE.',
        'MOINS DE BRUIT. PLUS D’INTENTION.',
        'LA DISCIPLINE FAÇONNE LA FLORAISON.'
      ]
    : [
        'NOTHING BLOOMS IN ITS COMFORT ZONE.',
        'BORN UNDER PRESSURE.',
        'LESS NOISE. MORE INTENT.',
        'DISCIPLINE SHAPES THE BLOOM.'
      ];

  const statusLabels = isFr
    ? ['INITIALISATION', 'PRÉPARATION DE L’EXPÉRIENCE', 'FINALISATION', 'PRÊT']
    : ['INITIALISING', 'PREPARING THE EXPERIENCE', 'FINALISING', 'READY'];
  const minimumDuration = isMobile ? 2300 : 2500;
  const maximumDuration = isMobile ? 3300 : 3500;
  const startedAt = performance.now();
  let finished = false;
  let revealScheduled = false;
  let rafId = 0;
  let displayedProgress = 0;
  let targetProgress = 4;
  let lastPaintedProgress = -1;
  let activeStatusIndex = -1;

  try { sessionStorage.setItem(KEY, '1'); } catch (error) {}

  if (quote) {
    const phraseIndex = Math.floor(Math.random() * phrases.length);
    quote.textContent = phrases[phraseIndex];
  }

  const setTarget = value => {
    targetProgress = Math.max(targetProgress, Math.min(99, value));
  };

  const paintProgress = value => {
    const normalized = Math.max(0, Math.min(100, value));
    const rounded = Math.floor(normalized);
    if (progress) {
      progress.textContent = rounded === 100
        ? '100%'
        : String(rounded).padStart(2, '0') + '%';
    }
    if (progressBar) {
      progressBar.style.transform = 'scaleX(' + (normalized / 100).toFixed(4) + ')';
    }
    if (progressTrack && rounded !== lastPaintedProgress) {
      progressTrack.setAttribute('aria-valuenow', String(rounded));
      lastPaintedProgress = rounded;
    }

    const nextStatusIndex = rounded >= 100 ? 3 : rounded >= 76 ? 2 : rounded >= 34 ? 1 : 0;
    if (status && nextStatusIndex !== activeStatusIndex) {
      status.textContent = statusLabels[nextStatusIndex];
      activeStatusIndex = nextStatusIndex;
    }
  };

  const markDomReady = () => setTarget(42);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markDomReady, {once:true});
  } else {
    markDomReady();
  }

  const preloadJobs = [];
  const conceptImages = runtime.stagingMode === true && runtime.launchPhase === 'open' && !isMobile
    ? Object.values(runtime.conceptImages || {}).filter(Boolean)
    : [];
  let assetsCompleted = 0;

  conceptImages.forEach(url => {
    preloadJobs.push(new Promise(resolve => {
      const image = new Image();
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        assetsCompleted += 1;
        setTarget(44 + (assetsCompleted / conceptImages.length) * 32);
        resolve();
      };
      image.onload = done;
      image.onerror = done;
      image.fetchPriority = 'high';
      image.decoding = 'async';
      image.src = url;
      if (image.complete) done();
    }));
  });

  const criticalImagesReady = preloadJobs.length
    ? Promise.race([
        Promise.allSettled(preloadJobs),
        new Promise(resolve => window.setTimeout(resolve, 1400))
    ]).then(() => setTarget(78))
    : Promise.resolve().then(() => setTarget(38));

  const pageReady = document.readyState === 'complete'
    ? Promise.resolve().then(() => setTarget(88))
    : new Promise(resolve => {
        window.addEventListener('load', () => {
          setTarget(88);
          resolve();
        }, {once:true});
      });

  const updateProgress = () => {
    if (finished) return;
    const elapsed = performance.now() - startedAt;
    const timelineRatio = Math.min(1, elapsed / minimumDuration);
    const timelineProgress = Math.min(94, 4 + 90 * (1 - Math.pow(1 - timelineRatio, 2)));
    const readinessBoost = Math.min(targetProgress, timelineProgress + 5);
    const desired = Math.min(99, Math.max(timelineProgress, readinessBoost));
    const distance = desired - displayedProgress;
    const smoothing = targetProgress >= 99 ? 0.16 : distance > 24 ? 0.095 : distance > 8 ? 0.075 : 0.055;
    displayedProgress = Math.max(displayedProgress, displayedProgress + distance * smoothing);
    paintProgress(displayedProgress);
    rafId = requestAnimationFrame(updateProgress);
  };

  const reveal = () => {
    if (finished) return;
    finished = true;
    if (rafId) cancelAnimationFrame(rafId);
    displayedProgress = 100;
    paintProgress(100);
    loader.setAttribute('aria-busy', 'false');
    loader.classList.add('is-complete');

    window.setTimeout(() => {
      loader.classList.remove('is-complete');
      root.classList.remove('dr-entry-pending');
      root.classList.add('dr-entry-reveal');
    }, 160);

    window.setTimeout(() => {
      root.classList.remove('dr-entry-reveal');
      loader.remove();
    }, 1080);
  };

  const scheduleReveal = () => {
    if (finished || revealScheduled) return;
    revealScheduled = true;
    setTarget(99);
    const remaining = Math.max(220, minimumDuration - (performance.now() - startedAt));
    window.setTimeout(reveal, remaining);
  };

  updateProgress();
  Promise.allSettled([pageReady, criticalImagesReady]).then(scheduleReveal);
  window.setTimeout(scheduleReveal, maximumDuration);
})();
